import React = require("react");
import * as dateMath from 'date-arithmetic';
import { Navigate } from "react-router-dom";
import styled from "styled-components";
import { AgendaMeetingActivityListing, rng } from "../../../amorg-common";
import { createActivity, getAgendaMeetingActivities, loadMeetingTemplate } from "../../../api/activityClientApi";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { isSameDate } from "../../../designSystem/formField/DateTimeStartEndFields";
import { Icon, iconName } from "../../../designSystem/icons/Icons";
import { SingleColumnLayout } from '../../../designSystem/layouts/SingleColumnLayout';
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";
import { TooltipTheOneWrapper } from "../../../designSystem/TooltipTheOne";
import { txt } from "../../../txt";
import { toDateString, toTimeString } from "../../../utils/date";
import { Button } from "../../../designSystem/button/Button";
import { PageHeader } from "../../../designSystem/PageHeader";
import { PageSectionTable } from "../../../designSystem/PageSectionTable";
import { OnOff } from "../../../designSystem/inputs/OnOff";
import { Modal } from "../../../designSystem/Modal";
import { P } from "../../../designSystem/typography/Body";
import { AgendaItemCounterCompositeIcon } from "../../../designSystem/compositeComponents/compositeIcons/AgendaItemCounterCompositeIcon";
import { ActionPointSummaryCounterCompositeIcon } from "../../../designSystem/compositeComponents/compositeIcons/ActionPointCounterCompositeIcon";


type State = {
	loading:boolean,
	activities:AgendaMeetingActivityListing[],
    redirectId:string
}

type AgendaMeetingListingViewProps = {
    type: 'WorkgroupMeeting' | 'AnnualWorkgroupStatus',
    
    currentActivitiesHeaderText:string,
    currentActivitiesHeaderIcon?:iconName,
    
    createNewButtonText:string,
    
    
    pastActivitiesHeaderText:string,
    pastActivitiesHeaderIcon?:iconName
}


export function AgendaMeetingListingView(props:AgendaMeetingListingViewProps): JSX.Element
{
	const [state, setState] = React.useState<State>({
        loading:true,
        activities:[],
        redirectId:''
    });
    const [showAnnualStatusCreateModal, setShowAnnualStatusCreateModal] = React.useState(false);

    React.useEffect(()=>{
        const getData = async () => {
            const activities = await getAgendaMeetingActivities(props.type);
            activities.sort((a,b)=> b.start.getTime() - a.start.getTime());
            // Get activities convert start and end from strings (JSON transport) to dates
            setState({activities, loading:false, redirectId:''});
        }
        getData();
    },[])

    function handleClickNavigate(activityId:string)
    {
        setState({...state, redirectId:activityId});
    }

    async function handleClickNew()
    {
        if(props.type === 'WorkgroupMeeting'){
            handleCreateNewMeeting();
        }
        else if(props.type === 'AnnualWorkgroupStatus'){
            setShowAnnualStatusCreateModal(true);
        }
    }

    async function handleCreateNewMeeting()
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()+1, 0, 0, 0);
        const endDate = dateMath.add(startDate, 1, 'hours');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            props.type,
            txt.getDynamic(`NewActivityType_${props.type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            setState({...state, redirectId:activityId});
        }
    }

    async function handleCreateNewAnnualStatus(useTemplate:boolean)
    {
        const now = new Date();

        const activityId = rng.getRandomId();
        const startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), now.getHours()+1, 0, 0, 0);
        const endDate = dateMath.add(startDate, 1, 'hours');

        setState({...state, loading:true});

        const result = await createActivity(
            activityId,
            props.type,
            txt.getDynamic(`NewActivityType_${props.type}`),
            '',
            startDate,
            endDate,
            ''
        );

        if(result.isSuccess){
            if(useTemplate){
                const loadResult = await loadMeetingTemplate(activityId, 'annual-meeting-standard');
                if(!loadResult.isSuccess){
                    setState({...state, loading:false});
                    return;
                }
            }
            setState({...state, redirectId:activityId});
            setShowAnnualStatusCreateModal(false);
        }
    }

    const current = state.activities.filter(activity => activity.start >= new Date());
    const past = state.activities.filter(activity => activity.start < new Date());

    const redirectUrl = props.type === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  (state.redirectId || '')
        : '/annualworkgroupstatus/' + (state.redirectId || '')

    return (
        <SingleColumnLayout
            noDivider
            contentContainer={
                <>
                    {state.redirectId !== '' &&
                        <Navigate to={redirectUrl} />
                    }

                    <LoadingPageOverlay loading={state.loading} />

                    <PageHeader 
                        headerIcon={props.currentActivitiesHeaderIcon}
                        headerText={props.currentActivitiesHeaderText}
                        headerStyle="secondary-gray"
                        buttons={<Button variant="filled" icon="actions_plus" text={props.createNewButtonText} onClick={handleClickNew} size={"42px"} emphasize />}
                    />
                    
                    <PageSectionTable
                        headerColumns={[
                            {text:'Agenda', gridDefinition:'80px'},
                            {text:'Titel', gridDefinition:'23%'},
                            {text:'Dato', gridDefinition:'17%'},
                            {text:'Lokation', gridDefinition:'28%'},
                            {text:'Deltagere', gridDefinition:'9%'},
                            {text:'', gridDefinition:'minmax(0, 1fr)'}
                        ]}
                        noContentText='Ingen aktuelle møder'
                        showAllLimit={5}>

                        {current.map(activity => {
                            
                            const participantTooltipContent = activity.participants && activity.participants.length > 0
                                ? (
                                    <>
                                        {activity.participants.map((participant, index) => (
                                            <div key={`${participant}-${index}`}>{participant}</div>
                                        ))}
                                    </>
                                )
                                : 'Ingen inviterede';

                            const scheduledTimeTooltipContent = !isSameDate(activity.start, activity.end) ? 
                                toDateString(activity.start) + " kl. " + toTimeString(activity.start) + " - " + toDateString(activity.end) + " kl. " + toTimeString(activity.end) 
                                : 
                                toDateString(activity.start) + " kl. " + toTimeString(activity.start) + " - " + toTimeString(activity.end);

                            return (
                                <React.Fragment key={activity.id}>

                                    <AgendaItemCounterCompositeIcon agendaitemCount={activity.agendaItemCount} />

                                    <ActivityFocus title={activity.title}><TruncatedText>{activity.title}</TruncatedText></ActivityFocus>

                                    <TooltipTheOneWrapper key={activity.id} tooltipContent={scheduledTimeTooltipContent}>
                                        <Flex flexDirection="row" alignItems="center" gap={4}>
                                            <Icon name="clock" />
                                            {toDateString(activity.start)} <b>{toTimeString(activity.start)}</b>
                                            
                                        </Flex>
                                    </TooltipTheOneWrapper>

                                    <ActivityFocus>
                                        <Icon name="interface_glyph_location" />
                                        <TruncatedText>
                                            {activity.location ? activity.location : 'Ingen lokation'}
                                        </TruncatedText>
                                    </ActivityFocus>

                                    <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                                        <Flex flexDirection="row" alignItems="center" gap={8}> 
                                        <Icon name="interface_user_team" />
                                        {activity.participantCount !== undefined ? activity.participantCount : 'Ingen inviterede'}
                                        </Flex>
                                    </TooltipTheOneWrapper>

                                    <ActivityReadMoreButton onClick={() => handleClickNavigate(activity.id)}>
                                        Læs <Icon name="arrows_chevron_chevronForward" />
                                    </ActivityReadMoreButton>
                                </React.Fragment>
                            );
                        })}
                    </PageSectionTable>
        
                    {past.length > 0 &&
                        <>
                        
                        <PageHeader 
                            headerIcon={props.pastActivitiesHeaderIcon}
                            headerText={props.pastActivitiesHeaderText}
                            headerStyle="secondary-gray" />

                        <PageSectionTable
                            headerColumns={[
                                {text:'Dato', gridDefinition:'17%'},
                                {text:'Titel', gridDefinition:'52%'},
                                {text:'Handling', gridDefinition:'7%'},
                                {text:'Deltagere', gridDefinition:'9%'},
                                {text:'', gridDefinition:'minmax(0, 1fr)'}
                            ]}
                            noContentText='Ingen tidligere møder'
                            showAllLimit={5}>
                            
                            {past.map(activity => {

                                const participantTooltipContent = activity.participants && activity.participants.length > 0
                                ? (
                                    <>
                                        {activity.participants.map((participant, index) => (
                                            <div key={`${participant}-${index}`}>{participant}</div>
                                        ))}
                                    </>
                                )
                                : 'Ingen deltagere';
            

                                return (
                                    <React.Fragment key={activity.id}>
                                        <Flex flexDirection="row" alignItems="center" gap={4}>
                                            <Icon name="clock" />
                                            {toDateString(activity.end)} {toTimeString(activity.end)}
                                        </Flex>
                                        
                                        <ActivityFocus title={activity.title}><TruncatedText>{activity.title}</TruncatedText></ActivityFocus>
                                        
                                        <ActionPointSummaryCounterCompositeIcon
                                            {...activity}
                                        />
                                        
                                        <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                                            <Flex flexDirection="row" alignItems="center" gap={8}> 
                                            <Icon name="interface_user_team" />
                                            {activity.participantCount !== undefined ? activity.participantCount : 'Ingen deltagere'}
                                            </Flex>
                                        </TooltipTheOneWrapper>
                                        
                                        <ActivityReadMoreButton onClick={() => handleClickNavigate(activity.id)}>
                                            Læs <Icon name="arrows_chevron_chevronForward" />
                                        </ActivityReadMoreButton>
                                        
                                        
                                        {/* <AmorgMenu {...props.menuProps} /> */}
                                        
                                    </React.Fragment>
                                );
                            })}
                        </PageSectionTable>

                        </>
                    }

                    <AnnualStatusCreateModal
                        show={showAnnualStatusCreateModal}
                        onClose={() => setShowAnnualStatusCreateModal(false)}
                        onCreate={handleCreateNewAnnualStatus}
                    />
                </>
            }
        />

	);
}

type AgendaMeetingViewProps = {
    activity:AgendaMeetingActivityListing
}

export function AgendaMeetingView({activity}:AgendaMeetingViewProps):JSX.Element
{
    const url = activity.activityType === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  activity.id
        : '/annualworkgroupstatus/' + activity.id

    return (
        <ActivityListingView
            title={activity.title}
            start={activity.start}
            end={activity.end}
            href={url}
            participantCount={activity.participantCount}
            participants={activity.participants}
            location={activity.location} 
            actionPointCount={activity.actionPointCount}
        />
    );
}




type ActivityListingViewProps = {
    title:string,
    start:Date,
    end:Date,
    href:string,
    participantCount?:number,
    participants?:string[],
    location?:string,
    actionPointCount?:number,
    actionPointDoneCount?:number,
    // menuProps:AmorgMenuProps,
    onClick?: () => void
}

export function ActivityListingView(props:ActivityListingViewProps):JSX.Element
{

    function handleOnClick(){
        document.location=props.href;
    }

    const participantTooltipContent = props.participants && props.participants.length > 0
        ? (
            <>
                {props.participants.map((participant, index) => (
                    <div key={`${participant}-${index}`}>{participant}</div>
                ))}
            </>
        )
        : 'Ingen inviterede';

    const scheduledTimeTooltipContent = !isSameDate(props.start, props.end) ? 
        toDateString(props.start) + " kl. " + toTimeString(props.start) + " - " + toDateString(props.end) + " kl. " + toTimeString(props.end) 
        : 
        toDateString(props.start) + " kl. " + toTimeString(props.start) + " - " + toTimeString(props.end);
                

    return (
        
        <ActivityRow>
            
            <Flex flexDirection="row" alignItems="center" gap={4}><Icon name="target" /> {props.actionPointCount}</Flex>
            
            <ActivityFocus title={props.title}><TruncatedText>{props.title}</TruncatedText></ActivityFocus>
                        
            <TooltipTheOneWrapper tooltipContent={scheduledTimeTooltipContent}>
                <Flex flexDirection="row" alignItems="center" gap={4}>
                    <Icon name="clock" />
                    {toDateString(props.start)} <b>{toTimeString(props.start)}</b>
                    
                </Flex>
            </TooltipTheOneWrapper>
            
            <ActivityFocus>
                <Icon name="interface_glyph_location" />
                <TruncatedText>
                    {props.location ? props.location : 'Ingen lokation'}
                </TruncatedText>
            </ActivityFocus>
                
            <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                <Flex flexDirection="row" alignItems="center" gap={8}> 
                <Icon name="interface_user_team" />
                {props.participantCount !== undefined ? props.participantCount : 'Ingen inviterede'}
                </Flex>
            </TooltipTheOneWrapper>
                                
            <ActivityReadMoreButton onClick={ handleOnClick }>
                Læs <Icon name="arrows_chevron_chevronForward" />
            </ActivityReadMoreButton>
            
            {/* <AmorgMenu {...props.menuProps} /> */}
            
        </ActivityRow>
    );
}



export function AgendaMeetingPastView({activity}:AgendaMeetingViewProps):JSX.Element
{
    const url = activity.activityType === 'WorkgroupMeeting' 
        ? '/workgroupmeetings/' +  activity.id
        : '/annualworkgroupstatus/' + activity.id

    return (
        <ActivityPastListingView
            title={activity.title}
            start={activity.start}
            end={activity.end}
            href={url}
            participantCount={activity.participantCount}
            participants={activity.participants}
            location={activity.location} 
            actionPointCount={activity.actionPointCount}
            actionPointDoneCount={activity.actionPointDoneCount}
        />
    );
}



export function ActivityPastListingView(props:ActivityListingViewProps):JSX.Element
{

    function handleOnClick(){
        document.location=props.href;
    }

    const participantTooltipContent = props.participants && props.participants.length > 0
        ? (
            <>
                {props.participants.map((participant, index) => (
                    <div key={`${participant}-${index}`}>{participant}</div>
                ))}
            </>
        )
        : 'Ingen deltagere';

    const actionPointTooltipContent = props.actionPointCount && props.actionPointCount > 0
        ? (
            <>
                {props.actionPointCount} handlepunkter, {props.actionPointDoneCount} afsluttede
            </>
        )
        : 'Ingen handlepunkter';
            

    return (
        
        <ActivityPastRow>
            
            <Flex flexDirection="row" alignItems="center" gap={4}>
                <Icon name="clock" />
                {toDateString(props.end)} {toTimeString(props.end)}
            </Flex>
            
            <ActivityFocus title={props.title}><TruncatedText>{props.title}</TruncatedText></ActivityFocus>
            
            <TooltipTheOneWrapper tooltipContent={actionPointTooltipContent}>
                <Flex flexDirection="row" alignItems="center" gap={8}> 
                <Icon name="target" />{props.actionPointDoneCount !== props.actionPointCount ? <b>{props.actionPointDoneCount}:{props.actionPointCount}</b> : `${props.actionPointDoneCount}:${props.actionPointCount}`}
                </Flex>
            </TooltipTheOneWrapper>
            
            <TooltipTheOneWrapper tooltipContent={participantTooltipContent}>
                <Flex flexDirection="row" alignItems="center" gap={8}> 
                <Icon name="interface_user_team" />
                {props.participantCount !== undefined ? props.participantCount : 'Ingen deltagere'}
                </Flex>
            </TooltipTheOneWrapper>
            
            <ActivityReadMoreButton onClick={ handleOnClick }>
                Læs <Icon name="arrows_chevron_chevronForward" />
            </ActivityReadMoreButton>
            
            
            {/* <AmorgMenu {...props.menuProps} /> */}
            
        </ActivityPastRow>
    );
}

function AnnualStatusCreateModal(props: { show: boolean; onCreate:(useTemplate:boolean) => void; onClose: () => void; }) {

    const [useTemplate, setUseTemplate] = React.useState(true);

    return (
        <Modal showModal={props.show} onCancel={props.onClose}
            title="Opret ny årlig drøftelse"
            content={
                <>
                    <P>Skal drøftelsen oprettes udfra den anbefalede mødeskabelon ?</P>
                    <P>Skabelonen er vejledende, og mødet kan frit tilpasses efter jeres behov.</P>

                    <Flex justifyContent="flex-start" gap={8}>
                        <OnOff active={useTemplate} onClick={setUseTemplate} />
                        <span>Anvend skabelon</span>
                    </Flex>

                    <Flex justifyContent="flex-end" gap={8}>
                        <Button text="Opret ny årlig drøftelse" variant={"filled"} onClick={() => { props.onCreate(useTemplate); } } />
                        <Button text="Annuller" variant="outlined" onClick={props.onClose} />
                    </Flex>
                </>
            }
        />
    );
}

const ActivityRow = styled.div`
    align-items: center;
    background: ${props => props.theme.colors.activityCard.background};
    display: grid;
    grid-template-columns: 5% 28% 17% 28% 9% minmax(0, 1fr);
    gap: 16px;
    border-bottom: 1px solid ${props => props.theme.colors.activityCard.border};
    padding: 0 8px 6px 8px;

    white-space: nowrap;  
`
export const ActivityRowHeader = styled(ActivityRow)`
    background: ${props => props.theme.colors.activityCard.headerBackground};
    color: ${props => props.theme.colors.activityCard.headerColor};
    padding: 8px;
    font-weight: bold;
    white-space: nowrap;
    border-bottom: 0;
`;

export const ActivityPastRow = styled.div`
    align-items: center;
    background: ${props => props.theme.colors.activityCard.background};
    display: grid;
    grid-template-columns: 17% 52% 7% 9% minmax(0, 1fr);
    gap: 16px;
    border-bottom: 1px solid ${props => props.theme.colors.activityCard.border};
    padding: 0 8px 6px 8px;

    white-space: nowrap;
`


export const ActivityPastRowHeader = styled(ActivityPastRow)`
    background: ${props => props.theme.colors.activityCard.headerBackground};
    color: ${props => props.theme.colors.activityCard.headerColor};
    padding: 8px;
    font-weight: bold;
    white-space: nowrap;
    border-bottom: 0;
`;


const ActivityFocus = styled.div`
font-size: 16px;
    color: ${props => props.theme.colors.text.normal};
    background: ${props => props.theme.colors.activityCard.backgroundHightlight};
    padding: 4px 8px;
    display: flex;
    align-items: center;
    flex-direction: row;     
min-width: 0;
    
`

const TruncatedText = styled.span`
    min-width: 0;
    overflow: hidden;
    flex: 1;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const ActivityReadMoreButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    padding: 4px 0px 4px 8px;
    cursor: pointer;
    width: 72px;
    justify-self: end;
    background:white;
    border: ${props => props.theme.colors.button.outlined.border};
    border-radius: 4px;

    &:hover {
        background: ${props => props.theme.colors.button.outlined.backgroundHover};
    }
`